import {
  Button,
  ConfigProvider,
  Dropdown,
  Flex,
  Menu,
  Popover,
  Space,
  Tag,
  theme,
  Typography,
} from 'antd'
import { Suspense, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styles from '../less/BaseLayout.module.less'
import userMenuStyles from '../less/UserMenu.module.less'
import {
  ArrowLeftOutlined,
  CaretDownOutlined,
  DownOutlined,
  GiftOutlined,
  LoadingOutlined,
  MenuOutlined,
} from '@ant-design/icons'
import { useProfilesSelector } from '../lib/profiles'
import { signOut } from 'firebase/auth'
import { useAuth, useUser } from 'reactfire'
import { BaseLayoutContext } from './BaseLayoutContext'
import * as Sentry from '@sentry/react'

import { useBreakpointContext } from '../lib/useBreakpointContext'
import { isDetourPath, useReturnPath } from '../lib/useReturnPath'
import { clearAllForUser as clearSetupDraftsForUser } from './MyProfile/setupDrafts'
import { Notifications } from './Notifications/Notifications'
import { LanguageSwitcher } from './LanguageSwitcher'
import { ThemeSwitcher } from './ThemeSwitcher'
import { useLogo, useResolvedTheme } from '../lib/themeController'
import { Help } from './Help/Help'
import { UserAvatar } from './UserAvatar'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'

export default function TopMenu() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const getReturnPath = useReturnPath()

  const [
    { selectedProfile, menu, homeLink, maintenance, focused, onReferClick },
  ] = useContext(BaseLayoutContext)

  const signOutOfApp = async () => {
    Sentry.setUser(null)

    // Setup-wizard drafts hold personal data; drop them on logout (best-effort).
    const signedOutUid = auth.currentUser?.uid
    if (signedOutUid) {
      clearSetupDraftsForUser(signedOutUid)
    }

    // Check if we're impersonating before signing out
    let isImpersonating = false
    try {
      const tokenResult = await auth.currentUser?.getIdTokenResult()
      isImpersonating = tokenResult?.claims?.impersonating === true
    } catch (_e) {
      // Ignore errors, proceed with normal logout
    }

    await signOut(auth)

    if (isImpersonating) {
      // Don't save returnUrl when logging out from impersonation
      window.location.replace('/login')
    } else {
      window.location.reload()
    }
  }

  const breakpoint = useBreakpointContext()

  let items = [
    {
      key: 'myAccount',
      label: (
        <Link to="/my-account/personal-data">
          {t('frontend:topMenu.myAccount')}
        </Link>
      ),
    },
    {
      key: 'theme',
      label: <ThemeSwitcher />,
      style: { cursor: 'default', background: 'transparent' },
    },
    { type: 'divider' },
    {
      key: 'signOut',
      label: (
        <Button block danger onClick={signOutOfApp}>
          {t('frontend:topMenu.logout')}
        </Button>
      ),
    },
  ]

  if (!breakpoint.lg) {
    items = [...(menu ?? []), ...items]
  }

  const { token } = theme.useToken()
  const logo = useLogo('full')
  const logoNoText = useLogo('noText')
  if (breakpoint.lg) {
    // Desktop
    return (
      <>
        <div className={styles.topMenu}>
          <div className={styles.topMenuInner}>
            <Link to={homeLink || `/`}>
              <img src={logo} alt="cleverlohn" className={styles.logo} />
            </Link>
            {!maintenance && !focused && (
              <Suspense fallback={<LoadingOutlined />}>
                <ProfileMenu
                  selectedProfile={selectedProfile}
                  breakpoint={breakpoint}
                  getReturnPath={getReturnPath}
                />
              </Suspense>
            )}
            {!maintenance && focused && selectedProfile && (
              <ProfileLabel
                selectedProfile={selectedProfile}
                breakpoint={breakpoint}
              />
            )}
            <Flex
              align="center"
              gap={token.marginXS}
              style={{ marginLeft: 'auto', paddingLeft: token.paddingLG }}
            >
              {maintenance ? (
                <Button danger onClick={signOutOfApp}>
                  {t('frontend:topMenu.logout')}
                </Button>
              ) : focused ? (
                <>
                  <LanguageSwitcher />
                  <Help text={t('frontend:common.help')} />
                  <Button onClick={signOutOfApp}>
                    {t('frontend:topMenu.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <LanguageSwitcher />
                  {onReferClick && (
                    <Button
                      type="text"
                      icon={<GiftOutlined />}
                      onClick={onReferClick}
                    >
                      {t('frontend:referral.topBarCta')}
                    </Button>
                  )}
                  <Help text={t('frontend:common.help')} />
                  <Suspense fallback={<LoadingOutlined />}>
                    <Notifications />
                  </Suspense>
                  <Dropdown
                    trigger={['click']}
                    placement="bottomRight"
                    menu={{ items }}
                    overlayStyle={{ minWidth: '200px' }}
                  >
                    <Space size="large" style={{ marginLeft: token.marginXL }}>
                      <div className={userMenuStyles.userMenu}>
                        <Suspense fallback={<LoadingOutlined />}>
                          <UserAvatarAndName />
                        </Suspense>
                        <DownOutlined
                          style={{
                            fontSize: '0.7em',
                            marginLeft: token.marginXXS,
                          }}
                        />
                      </div>
                    </Space>
                  </Dropdown>
                </>
              )}
            </Flex>
          </div>
        </div>
      </>
    )
  } else {
    // Mobile
    return (
      <div className={styles.topMenu}>
        <Link to={homeLink || `/`}>
          <img
            src={logoNoText}
            alt="cleverlohn"
            className={styles.logoNoText}
          />
        </Link>
        {!maintenance && !focused && (
          <Suspense fallback={<LoadingOutlined />}>
            <ProfileMenu
              selectedProfile={selectedProfile}
              breakpoint={breakpoint}
              getReturnPath={getReturnPath}
            />
          </Suspense>
        )}
        {!maintenance && focused && selectedProfile && (
          <ProfileLabel
            selectedProfile={selectedProfile}
            breakpoint={breakpoint}
          />
        )}
        <Flex
          align="center"
          gap={token.marginXS}
          style={{ marginLeft: 'auto', paddingLeft: token.paddingSM }}
        >
          {maintenance ? (
            <Button danger onClick={signOutOfApp}>
              {t('frontend:topMenu.logout')}
            </Button>
          ) : focused ? (
            <>
              <LanguageSwitcher />
              <Help />
              <Button onClick={signOutOfApp}>
                {t('frontend:topMenu.logout')}
              </Button>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              {onReferClick && (
                <Button
                  type="text"
                  aria-label={t('frontend:referral.topBarCta')}
                  icon={<GiftOutlined />}
                  onClick={onReferClick}
                />
              )}
              <Help />
              <Suspense fallback={<LoadingOutlined />}>
                <Notifications />
              </Suspense>
              <Dropdown
                placement="bottomRight"
                trigger={['click']}
                menu={{
                  items,
                  selectedKeys: [location.pathname],
                  onClick: ({ key }) => {
                    if (key.startsWith('/')) {
                      navigate(key)
                    }
                  },
                }}
                overlayStyle={{ minWidth: '200px' }}
              >
                <Button
                  type="text"
                  aria-label="Menu"
                  icon={<MenuOutlined style={{ fontSize: 22 }} />}
                  style={{ marginLeft: token.marginSM }}
                />
              </Dropdown>
            </>
          )}
        </Flex>
      </div>
    )
  }
}

function UserAvatarAndName() {
  const user = useUser()

  const userName = user.data.displayName || user.data.email || null

  return (
    <>
      <UserAvatar uid={user.data.uid} name={userName} />
      <span className={userMenuStyles.name}>{userName}</span>
    </>
  )
}

function ProfileLabel({ selectedProfile, breakpoint, inactive = false }) {
  return (
    <div className={styles.profileMenu}>
      <Typography.Text
        style={{
          flexGrow: 0,
          flexShrink: 1,
          textDecoration: inactive ? 'line-through' : 'none',
        }}
        ellipsis
      >
        {selectedProfile.label}
      </Typography.Text>
      {breakpoint.md && selectedProfile.tag && (
        <Tag style={{ margin: '0 0 0 10px' }} color={selectedProfile.tagColor}>
          {selectedProfile.tag}
        </Tag>
      )}
    </div>
  )
}

function ProfileMenu({ selectedProfile, breakpoint, getReturnPath }) {
  const profiles = useProfilesSelector()
  const location = useLocation()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Without a previous page, fall back to the first active profile
  const defaultProfile = profiles.find((p) => !p.inactive) ?? profiles[0]

  if (isDetourPath(location.pathname)) {
    return (
      <Button
        onClick={() => navigate(getReturnPath() ?? defaultProfile?.link ?? '/')}
        icon={<ArrowLeftOutlined />}
      >
        {t('frontend:common.back')}
      </Button>
    )
  }

  // CL-528 FIX: Find the profile matching current URL and use ITS inactive status.
  // This prevents showing stale selectedProfile data during navigation, since
  // React Router updates location before the Employee component can update selectedProfile.
  // Using inline textDecoration instead of Typography.Text's `delete` prop because
  // the `delete` prop doesn't re-render when only that prop changes but label stays the same.
  const currentProfile = profiles.find((p) =>
    location.pathname.startsWith(p.link.replace('/dashboard', ''))
  )
  const effectiveInactive =
    currentProfile?.inactive ?? selectedProfile?.inactive

  if (!selectedProfile) {
    return null
  }

  if (profiles.length === 1) {
    return (
      <ProfileLabel
        selectedProfile={selectedProfile}
        breakpoint={breakpoint}
        inactive={effectiveInactive}
      />
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => setOpen(newOpen)}
      overlayInnerStyle={{
        paddingLeft: token.paddingXXS,
        paddingRight: token.paddingXXS,
        paddingBlock: token.paddingXXS,
        overflow: 'hidden',
      }}
      content={
        <ProfileMenuContent onClick={() => setOpen(false)} items={profiles} />
      }
      trigger="click"
      arrow={false}
      placement="bottomLeft"
    >
      <Button className={styles.profileMenu}>
        <Typography.Text
          style={{
            flexGrow: 0,
            flexShrink: 1,
            textDecoration: effectiveInactive ? 'line-through' : 'none',
          }}
          ellipsis
        >
          {selectedProfile?.label}
        </Typography.Text>
        {breakpoint.md && selectedProfile?.tag && (
          <Tag
            style={{ margin: '0 0 0 10px' }}
            color={selectedProfile?.tagColor}
          >
            {selectedProfile?.tag}
          </Tag>
        )}
        <CaretDownOutlined style={{ fontSize: token.fontSizeSM }} />
      </Button>
    </Popover>
  )
}

function ProfileMenuContent({ items, onClick }) {
  const { token } = theme.useToken()
  const resolvedTheme = useResolvedTheme()
  return (
    <OverlayScrollbarsComponent
      defer
      options={{
        scrollbars: {
          theme: resolvedTheme === 'dark' ? 'os-theme-light' : 'os-theme-dark',
        },
      }}
      style={{ maxHeight: 'calc(100vh - 100px)' }}
    >
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              itemHeight: 32,
              itemMarginBlock: 0,
              itemMarginInline: 0,
              itemBorderRadius: token.borderRadiusSM,
              itemSelectedBg: 'transparent',
              itemSelectedColor: 'inherit',
            },
          },
          token: {
            padding: token.paddingSM,
          },
        }}
      >
        <Menu
          mode="vertical"
          size="small"
          style={{ border: 'none' }}
          onClick={onClick}
          items={items.map(({ key, label, link, tag, tagColor, inactive }) => ({
            key: key,
            label: (
              <Link
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  columnGap: '10px',
                  textDecoration: inactive ? 'line-through' : 'none',
                }}
                to={link}
              >
                {label}
                <span>
                  {tag && (
                    <Tag style={{ margin: 0 }} color={tagColor}>
                      {tag}
                    </Tag>
                  )}
                </span>
              </Link>
            ),
          }))}
        />
      </ConfigProvider>
    </OverlayScrollbarsComponent>
  )
}
